import { useState } from "react";
import { downloadReport } from "../../services/analysisService";
import { AnalysisInput } from "../../types/analysisPayload";
import Modal from "../modal/modal.tsx";
import { PRIVACY_VERSION } from "../modal/modal.tsx";
import "./download.css";

interface DownloadProps {
  isOpen: boolean;
  onClose: () => void;
  analysisInput: AnalysisInput;
  analysisResults: unknown[];
}

const Download = ({ isOpen, onClose, analysisInput, analysisResults }: DownloadProps) => {
  const [email, setEmail] = useState("");
  const [occupation, setOccupation] = useState("");
  const [usage, setUsage] = useState("");
  const [consent, setConsent] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canDownload =
    isEmailValid &&
    occupation.trim().length > 0 &&
    usage.trim().length > 0 &&
    usage.trim().length <= 1000 &&
    consent &&
    !isGenerating;

  const handleDownloadPDF = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canDownload) return;

    setIsGenerating(true);
    try {
      const reportBody = {
        plantName: analysisInput.wtfName,
        email: email.trim(),
        occupation: occupation.trim(),
        usage: usage.trim(),
        analysis_input: analysisInput,
        analysis_results: analysisResults,
      };

      const response = await downloadReport(reportBody);
      const blob = response.data;
      const suggestedName = "wastewise-report.pdf";

      const saveFilePicker = (window as any).showSaveFilePicker;
      if (saveFilePicker) {
        try {
          const fileHandle = await saveFilePicker({
            suggestedName,
            types: [
              {
                description: "PDF Report",
                accept: { "application/pdf": [".pdf"] },
              },
            ],
          });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          handleClose();
          return;
        } catch (err: any) {
          // If the user canceled the picker, do nothing.
          if (err?.name === "AbortError") return;
          console.error("Error using save file picker:", err);
        }
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = suggestedName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      handleClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setOccupation("");
    setUsage("");
    setConsent(false);
    setShowPrivacy(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="download-overlay" onClick={handleClose}>
      <div className="download-wrapper">
        <button className="download-close" onClick={handleClose}>
                                                                   ×
        </button>
        <div className="download-modal" onClick={(e) => e.stopPropagation()}>
          <div className="download-content">
            <h2 className="download-title">Download Report</h2>

            <form className="download-form">
              <div className="download-input-group">
                <input
                  type="email"
                  placeholder="Insert your email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="download-input"
                  required
                />
              </div>

              <div className="download-input-group">
                <input
                  type="text"
                  placeholder="Your Occupation *"
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                  className="download-input"
                  required
                />
              </div>

              <div className="download-input-group">
                <textarea
                  placeholder="How are you going to use this analysis? *"
                  value={usage}
                  onChange={(e) => setUsage(e.target.value)}
                  className="download-input"
                  required
                  rows={5}
                  maxLength={1000}
                  style={{ resize: "vertical" }}
                />
              </div>

              <p className="download-purpose">
                We store your email, occupation and stated use together with
                the plant name to track uptake and improve WasteWise. Visible
                to IWMI admins only. See{" "}
                <button
                  type="button"
                  className="download-privacy-link"
                  onClick={() => setShowPrivacy(true)}
                >
                  Privacy
                </button>
                .
              </p>

              <label className="download-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="download-consent-checkbox"
                />
                <span>
                  I consent to IWMI storing my email, occupation and use
                  statement for report-download analytics as described in the
                  Privacy notice (v{PRIVACY_VERSION}). I can request deletion
                  at any time.
                </span>
              </label>

              <div className="download-buttons">
                <button
                  type="button"
                  className="download-button"
                  onClick={handleDownloadPDF}
                  disabled={!canDownload}
                >
                  {isGenerating ? "Generating..." : "Download PDF →"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    {showPrivacy && (
      <Modal
        type="privacy"
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
    )}
    </>
  );
};

export default Download;
